import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpContext,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  map,
  Observable,
  catchError,
  tap,
  throwError,
  of,
  shareReplay,
  finalize,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { SKIP_GLOBAL_LOADING } from './global-loading.context';
import {
  User,
  AddOfferedTripResponse,
  Brand,
  Color,
  DriverCar,
  OfferedTrip,
  Role,
  ReservedTrip,
  CarModel, // 👈 import your class
  UserSubscription,
  TripReview,
  FavoriteDriver,
} from './rest.classes';

/* ============================================================
 * Base REST
 * ============================================================ */
export abstract class AbstractRestService<T> {
  public apiServerUrl = environment.apiUrl;
  protected actionUrl(): string {
    return `${this.apiServerUrl}/${this.action}`;
  }

  constructor(
    protected http: HttpClient,
    protected action: string,
  ) {}

  getAll(): Observable<T[]> {
    return this.http
      .get<T[]>(this.actionUrl())
      .pipe(catchError(this.handleError.bind(this)));
  }

  getOne(id: number): Observable<T> {
    return this.http
      .get<T>(`${this.actionUrl()}/${id}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  create(object: T): Observable<T> {
    return this.http
      .post<T>(this.actionUrl(), object)
      .pipe(catchError(this.handleError.bind(this)));
  }

  update(id: number, object: any): Observable<T> {
    return this.http
      .put<T>(`${this.actionUrl()}/${id}`, object)
      .pipe(catchError(this.handleError.bind(this)));
  }

  delete(id: number): Observable<any> {
    return this.http
      .delete(`${this.actionUrl()}/${id}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  softDelete(id: number): Observable<any> {
    return this.http
      .patch(`${this.actionUrl()}/${id}/soft-delete`, {})
      .pipe(catchError(this.handleError.bind(this)));
  }

  restore(id: number): Observable<any> {
    return this.http
      .patch(`${this.actionUrl()}/${id}/restore`, {})
      .pipe(catchError(this.handleError.bind(this)));
  }

  protected handleError(error: HttpErrorResponse): Observable<never> {
    console.error('API Error:', error);
    let errorMessage = 'An unknown error occurred!';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }
}

/* ============================================================
 * Auth / Login
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class LoginService extends AbstractRestService<User> {
  constructor(protected override http: HttpClient) {
    super(http, 'login');
  }

  getNotificationPreferences() {
    return this.http.get<any>(
      this.actionUrl() + '/user/notification-preferences',
    );
  }

  updateNotificationPreferences(notifications: {
    email: boolean;
    whatsapp: boolean;
    push: boolean;
  }): Observable<any> {
    return this.http.post(this.actionUrl() + '/user/notifications', {
      notifications,
    });
  }

  getUserById(id: number, tripId?: number | null): Observable<any> {
    let params: Record<string, string> = {};
    if (tripId) {
      params['trip_id'] = String(tripId);
    }
    return this.http.get(`${this.actionUrl()}/users/${id}`, { params });
  }
  uploadPhoto_profile(formData: FormData) {
    return this.http.post<{ id: number; url: string }>(
      `${this.actionUrl()}/upload-photo-profile`,
      formData,
    );
  }

  destroyUserPhoto(
    userId: number,
    data: { type: 'identity' | 'driver_license'; slot: number },
  ) {
    return this.http.delete(`${this.actionUrl()}/users/${userId}/photos`, {
      body: data,
    });
  }

  updateUserPhotos(
    userId: number,
    data: { type: string; slot: number; photo_id: number },
  ) {
    return this.http.put<{ message: string; photos: (string | null)[] }>(
      `${this.actionUrl()}/${userId}/photos`,
      data,
      {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      },
    );
  }

  getPhoto(filename: string, skipGlobalLoading = false): Observable<Blob> {
    const filePath = encodeURIComponent(
      String(filename || '').replace(/\\/g, '/'),
    );
    return this.http
      .get(`${this.actionUrl()}/photos?filePath=${filePath}`, {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, skipGlobalLoading),
        responseType: 'blob',
      })
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to get car image'),
          ),
        ),
      );
  }

  login(data: any): Observable<any> {
    return this.http
      .post(this.actionUrl(), {
        identifier: data.identifier,
        password: data.password,
      })
      .pipe(
        tap((res: any) => {
          if (res.user?.id) localStorage.setItem('userId', String(res.user.id));
        }),
        map((res: any) => {
          localStorage.setItem('auth_token', res.token);
          return res;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => new Error(err.error?.message || 'Login error')),
        ),
      );
  }

  register(formData: FormData): Observable<any> {
    return this.http.post(this.actionUrl() + '/register', formData).pipe(
      map((res: any) => res),
      catchError((err: HttpErrorResponse) =>
        throwError(
          () => new Error(err.error?.message || 'Registration failed'),
        ),
      ),
    );
  }

  googleLogin(
    token: string,
    profile?: { full_name?: string; first_name?: string; last_name?: string },
    accessToken?: string,
  ): Observable<{ token: string; user: any }> {
    const body: any = { token };
    if (accessToken && accessToken !== token) {
      body.access_token = accessToken;
    }
    if (profile) {
      body.full_name = profile.full_name;
      body.first_name = profile.first_name;
      body.last_name = profile.last_name;
    }

    return this.http
      .post<{
        token: string;
        user: any;
      }>(this.actionUrl() + '/auth/social-login', body)
      .pipe(
        tap((res) => {
          localStorage.setItem('auth_token', res.token);
          localStorage.setItem('userId', String(res.user.id));
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Google login failed'),
          ),
        ),
      );
  }

  appleLogin(token: string, profile?: { full_name?: string; first_name?: string; last_name?: string }): Observable<{ token: string; user: any }> {
    const body: any = { token };
    if (profile) {
      body.full_name = profile.full_name;
      body.first_name = profile.first_name;
      body.last_name = profile.last_name;
    }

    return this.http
      .post<{
        token: string;
        user: any;
      }>(this.actionUrl() + '/auth/apple-login', body)
      .pipe(
        tap((res) => {
          localStorage.setItem('auth_token', res.token);
          localStorage.setItem('userId', String(res.user.id));
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Apple login failed'),
          ),
        ),
      );
  }

  logout(): Observable<any> {
    return this.http.post(this.actionUrl() + '/logout', {}).pipe(
      tap(() => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('userId');
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => new Error(err.error?.message || 'Logout failed')),
      ),
    );
  }

  verifyEmail(data: {
    email: string;
    verification_code: string;
  }): Observable<any> {
    return this.http.post(`${this.actionUrl()}/verify-email`, data);
  }
  resendVerificationCode(data: { email: string }): Observable<any> {
    return this.http.post(`${this.actionUrl()}/resend-verification`, data);
  }
  sendResetCode(data: { method: 'email' | 'phone'; email?: string; phone?: string }) {
    return this.http.post(`${this.actionUrl()}/send-reset-code`, data);
  }
  resetPassword(data: any) {
    return this.http.post(`${this.actionUrl()}/reset-password`, data);
  }

  getUserProfile(userId?: string, skipGlobalLoading = false): Observable<any> {
    let params = {};
    if (userId) {
      params = { userID: userId };
    }

    return this.http
      .get(this.actionUrl() + '/user', {
        params: params,
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, skipGlobalLoading),
      })
      .pipe(
        map((res: any) => res),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to get profile'),
          ),
        ),
      );
  }
  public getProfile(): Observable<any> {
    return this.http.get(this.actionUrl() + '/profile', {}).pipe(
      map((res: any) => res),
      catchError((err: HttpErrorResponse) => {
        // Added HttpErrorResponse type
        console.error('Get profile error:', err);
        return throwError(
          () => new Error(err.error?.message || 'Failed to get profile'),
        );
      }),
    );
  }

  loadCarOptions(): Observable<any> {
    return this.http.get(this.actionUrl() + '/cars').pipe(
      map((res: any) => res),
      catchError((err: HttpErrorResponse) =>
        throwError(
          () => new Error(err.error?.message || 'Failed to load car options'),
        ),
      ),
    );
  }

  getProfileImage(filePath: string, skipGlobalLoading = true): Observable<Blob> {
    const normalized = String(filePath || '').replace(/\\/g, '/');
    const params = new HttpParams().set('filePath', normalized);

    return this.http.get(`${this.actionUrl()}/profile_photo`, {
      responseType: 'blob',
      params,
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, skipGlobalLoading),
    });
  }
  updateProfile(data: any): Observable<any> {
    return this.http
      .post(this.actionUrl() + '/update_profile', data, {})
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to update profile'),
          ),
        ),
      );
  }

  checkEmailExists(email: string): Observable<boolean> {
    return this.http
      .post<boolean>(this.actionUrl() + '/check-email', { email })
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () =>
              new Error(
                err.error?.message || 'Failed to check email existence',
              ),
          ),
        ),
      );
  }

  checkPhoneExists(phone: string): Observable<{ exists: boolean }> {
    return this.http
      .get<{
        exists: boolean;
      }>(`${this.actionUrl()}/check-phone?phone=${encodeURIComponent(phone)}`)
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () =>
              new Error(
                err.error?.message || 'Failed to check phone existence',
              ),
          ),
        ),
      );
  }
  uploadProfilePicture(
    userId: string,
    media_id: number,
  ): Observable<{ message: string; profile_picture?: string; profile_picture_url?: string }> {
    return this.http.post<{ message: string; profile_picture?: string; profile_picture_url?: string }>(
      `${this.actionUrl()}/${userId}/profile-picture`,
      { media_id },
    );
  }

  deleteProfilePicture(): Observable<any> {
    return this.http.delete(`${this.actionUrl()}/delete-photo`, {});
  }
}

/* ============================================================
 * Users
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class UserService extends AbstractRestService<User> {
  constructor(http: HttpClient) {
    super(http, 'users');
  }

  updateDocumentVerification(
    userId: number,
    type: 'identity' | 'driver_license',
    status: 'pending' | 'approved' | 'rejected',
  ) {
    return this.http.patch(
      `${this.apiServerUrl}/admin/users/${userId}/documents`,
      { type, status },
      { context: new HttpContext().set(SKIP_GLOBAL_LOADING, true) },
    );
  }

  getAdminStats() {
    return this.http.get<{
      totalUsers: number;
      totalCars: number;
      totalDrivers: number;
      totalTrips: number;
      activeTrips: number;
      deletedTrips: number;
    }>(`${this.apiServerUrl}/admin/stats`, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }

  getAdminUsers() {
    return this.http.get<User[]>(`${this.apiServerUrl}/admin/users`, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }

  getAdminSubmissions() {
    return this.http.get<any[]>(`${this.apiServerUrl}/admin/submissions`, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }

  // request OTP
  requestDeleteOtp(userId: number) {
    return this.http.post(
      `${this.actionUrl()}/${userId}/request-delete-otp`,
      {},
    );
  }

  deleteNotification(notificationId: string) {
    return this.http.post(
      `${this.actionUrl()}/${notificationId}/deleteNotification`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  deleteAllNotifications() {
    return this.http.post(
      `${this.actionUrl()}/deleteNotifications`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  getNotifications() {
    return this.http.get(`${this.actionUrl()}/Notifications`, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      withCredentials: true,
    });
  }

  deleteUser(id: number, type: 'soft' | 'wipe', code: string) {
    // Body
    const body = { type, code }; // send the OTP code along with the type
    return this.http.post(`${this.actionUrl()}/${id}/delete`, body);
  }
  // Send OTP to phone
  sendOtp(payload: { phone: string }): Observable<any> {
    return this.http.post(`${this.actionUrl()}/send-otp`, payload);
  }
  sendNewOtp(payload: { new_phone: string }): Observable<any> {
    return this.http.post(`${this.actionUrl()}/send-new-otp`, payload);
  }
  // Verify OTP
  verifyOtp(payload: {
    phone: string;
    verification_code: string;
  }): Observable<any> {
    return this.http.post(`${this.actionUrl()}/verify-otp`, payload);
  }

  verifyNewOtp(payload: {
    verification_code: string;
    phone: string;
  }): Observable<any> {
    return this.http.post(`${this.actionUrl()}/verify-new-otp`, payload);
  }

  setPasscode(payload: any) {
    return this.http.post(`${this.actionUrl()}/set-passcode`, payload, {});
  }

  email_login(email: string, lang: string = 'en'): Observable<any> {
    return this.http.post(`${this.actionUrl()}/register-email`, {
      email,
      lang,
    });
  }

  verify_email(payload: { user_id: number | null; verification_code: string }) {
    return this.http.post(`${this.actionUrl()}/verify-email-reg`, payload);
  }

  VerifyEmailCode(data: any): Observable<any> {
    return this.http.post(`${this.actionUrl()}/verify-email-code`, data);
  }

  sendEmailCode(email: any) {
    return this.http.post(`${this.actionUrl()}/send-email-code`, email);
  }

  getAllUsers(): Observable<User[]> {
    return this.http
      .get<User[]>(`${this.apiServerUrl}/admin/users`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  updateUser(updatedUser: any): Observable<any> {
    const userId = updatedUser.id;
    return this.http.put<any>(
      `${this.actionUrl()}/${userId}/update-role-and-status`,
      updatedUser,
    );
  }

  DeleteUser(id: number): Observable<any> {
    return this.http
      .patch(`${this.actionUrl()}/${id}/soft-delete`, {})
      .pipe(catchError(this.handleError.bind(this)));
  }

  getUser(): Observable<any> {
    const savedUser = localStorage.getItem('user');
    const user = savedUser ? JSON.parse(savedUser) : null;

    if (!user) {
      return of(null);
    }

    return this.http
      .patch(`${this.actionUrl()}/getUser`, {
        id: user.id,
        email: user.email,
        phone_number: user.phone_number,
        full_name: user.full_name,
      }, {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  updateLanguage(lang: string) {
    return this.http.post(
      `${this.actionUrl()}/update-language`,
      { lang },
      { context: new HttpContext().set(SKIP_GLOBAL_LOADING, true) },
    );
  }
}

/* ============================================================
 * Roles
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class RoleService extends AbstractRestService<Role> {
  constructor(http: HttpClient) {
    super(http, 'roles');
  }

  getAllRoles(): Observable<Role[]> {
    return this.http
      .get<Role[]>(this.actionUrl(), {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      })
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to get all roles'),
          ),
        ),
      );
  }

  createRole(roleData: { name: string }): Observable<Role> {
    return this.http
      .post<Role>(this.actionUrl(), roleData)
      .pipe(catchError(this.handleError.bind(this)));
  }

  updateRole(id: number, roleData: { name: string }): Observable<Role> {
    return this.http
      .put<Role>(`${this.actionUrl()}/${id}`, roleData)
      .pipe(catchError(this.handleError.bind(this)));
  }

  deleteRole(id: number): Observable<any> {
    return this.http
      .delete(`${this.actionUrl()}/${id}`)
      .pipe(catchError(this.handleError.bind(this)));
  }
}

/* ============================================================
 * Brands / Colors
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class BrandService extends AbstractRestService<Brand> {
  constructor(http: HttpClient) {
    super(http, 'brands');
  }

  modelsByBrand(id: string): Observable<any> {
    return this.http
      .get(`${this.actionUrl()}/${id}/models`, {})
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to load models'),
          ),
        ),
      );
  }
}

@Injectable({ providedIn: 'root' })
export class ColorService extends AbstractRestService<Color> {
  constructor(http: HttpClient) {
    super(http, 'colors');
  }
}

/* ============================================================
 * Models (Car Models)  👈 NEW
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class ModelService extends AbstractRestService<CarModel> {
  constructor(http: HttpClient) {
    super(http, 'models');
  }

  /** GET /models?brandId=... */
  list(brandId?: number | string): Observable<CarModel[]> {
    let params = new HttpParams();
    if (brandId !== undefined && brandId !== null && brandId !== '') {
      params = params.set('brandId', String(brandId));
    }
    return this.http
      .get<CarModel[]>(this.actionUrl(), {
        params,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }
}

/* ============================================================
 * Driver Cars
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class DriverCarService extends AbstractRestService<DriverCar> {
  private carImageCache = new Map<string, Observable<Blob>>();

  constructor(http: HttpClient) {
    super(http, 'driver-cars');
  }

  // Override getAll to use the correct endpoint that returns paginated data
  override getAll(): Observable<any> {
    return this.http
      .get<any>(`${this.apiServerUrl}/admin/driver-cars`, {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  updateCarLicenseVerification(
    carId: number,
    status: 'pending' | 'approved' | 'rejected',
  ) {
    return this.http.patch(
      `${this.apiServerUrl}/admin/driver-cars/${carId}/license-verification`,
      { status },
      { context: new HttpContext().set(SKIP_GLOBAL_LOADING, true) },
    );
  }

  destroyCarPhoto(
    carId: number,
    data: { type: 'license' | 'cars'; slot: number; path?: string },
  ) {
    return this.http.delete<{ message: string; photos: (string | null)[] }>(
      `${this.actionUrl()}/car-delete/${carId}/photos`,
      { body: data },
    );
  }

  updateCarPhotos(
    carId: number,
    data: { type: string; slot: number; photo_id: number },
  ) {
    return this.http.put<{ message: string; photos: (string | null)[] }>(
      `${this.actionUrl()}/cars_photos/${carId}/photos`,
      data,
      {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      },
    );
  }

  DestroyCarDetails(
    userId: number,
    data: { type: 'license' | 'cars'; slot: number },
  ) {
    return this.http.delete(`${this.actionUrl()}/users/${userId}/photos`, {
      body: data,
    });
  }

  addCarPhoto(carId: number, photoId: number) {
    return this.http.put<{
      message: string;
      photo_path: string;
      car_photos: (string | null)[];
    }>(`${this.actionUrl()}/cars/${carId}/photos`, {
      photo_id: photoId,
    }, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }
  addLicensePhoto(carId: number, photoId: number) {
    return this.http.put(`${this.actionUrl()}/${carId}/photos`, {
      photo_id: photoId,
    }, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }

  deletePhotoByPath(path: string) {
    return this.http.delete(`${this.actionUrl()}/photos`, {
      body: { path },
    });
  }

  getCarbyId(id: string, userId: number | null, tripId?: number | null) {
    // Build query params
    let params: any = {};
    if (userId !== null) {
      params.user_id = userId;
    }
    if (tripId) {
      params.trip_id = tripId;
    }

    return this.http
      .get<any>(`${this.actionUrl()}/${id}`, {
        params: params,
      })
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to load car'),
          ),
        ),
      );
  }

  uploadPhoto(formData: FormData) {
    return this.http.post<{ id: number; url: string }>(
      `${this.actionUrl()}/upload-photo`,
      formData,
      {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      },
    );
  }
  updateCarLicensePhoto(carId: number, photoId: number) {
    return this.http.put(`${this.actionUrl()}/${carId}/license-photo`, {
      photo_id: photoId,
    });
  }
  deleteCarPhoto(payload: { car_id: number; path: string }) {
    return this.http.delete(
      `${this.actionUrl()}/delete-car-photo`,
      { body: payload }, // important: DELETE with body
    );
  }
  getCarImage(filename: string, skipGlobalLoading = true): Observable<Blob> {
    const filePath = String(filename || '').replace(/\\/g, '/');
    const cachedImage = this.carImageCache.get(filePath);

    if (cachedImage) {
      return cachedImage;
    }

    const request$ = this.http
      .get(`${this.actionUrl()}/car-photo?filePath=${encodeURIComponent(filePath)}`, {
        responseType: 'blob',
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, skipGlobalLoading),
      })
      .pipe(
        map((blob: Blob) => {
          if (
            blob?.type &&
            blob.type !== 'application/octet-stream' &&
            !blob.type.startsWith('image/')
          ) {
            throw new Error('Failed to get car image');
          }
          return blob;
        }),
        catchError((err: HttpErrorResponse) => {
          this.carImageCache.delete(filePath);
          return throwError(
            () => new Error(err.error?.message || 'Failed to get car image'),
          );
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.carImageCache.set(filePath, request$);
    return request$;
  }
  // getUserCars(): Observable<any> {
  //     ;
  //     return this.http
  //       .get(this.actionUrl() + '/userCars', {
  //
  //       })
  //       .pipe(
  //         map((res: any) => res.cars),
  //         catchError((err: HttpErrorResponse) =>
  //           throwError(() => new Error(err.error?.message || 'Failed to get user cars')),
  //         ),
  //       );
  //   }

  getUserCars(
    userId?: number | null,
    skipGlobalLoading = true,
  ): Observable<any> {
    // Base URL
    let url = `${this.actionUrl()}`;

    // Append user_id only if provided
    if (userId) {
      url += `?user_id=${userId}`;
    }

    return this.http.get(url, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, skipGlobalLoading),
    }).pipe(
      map((res: any) => res.cars), // Return only the cars array
      catchError((err: HttpErrorResponse) => {
        const msg = err.error?.message || 'Failed to get user cars';
        return throwError(() => new Error(msg));
      }),
    );
  }
  addCar(data: any): Observable<any> {
    return this.http.post(this.actionUrl() + '/register_driver', data).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error('Add car error:', err);
        return throwError(() => err);
      }),
    );
  }

  destroyCar(id: number): Observable<any> {
    return this.http
      .delete(`${this.actionUrl()}/destroy/${id}`, {})
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to delete car'),
          ),
        ),
      );
  }

  checkCarTrips(carId: number): Observable<any> {
    return this.http
      .get<any>(`${this.actionUrl()}/${carId}/trips`)
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to check car trips'),
          ),
        ),
      );
  }

  updateCar(id: number, carData: any): Observable<any> {
    return this.http
      .put(`${this.actionUrl()}/update/${id}`, carData, {})
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to update car'),
          ),
        ),
      );
  }

  deleteLicensePicture(): Observable<any> {
    return this.http.delete(`${this.actionUrl()}/delete-photo`, {});
  }
}

/* ============================================================
 * Offered Trips (incl. Admin)
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class OfferedTripService extends AbstractRestService<OfferedTrip> {
  private userTripsCache = new Map<number, any[]>();
  private userTripsAllCache = new Map<number, any[]>();
  private userTripsInFlight = new Map<number, Observable<any[]>>();
  private userTripsAllInFlight = new Map<number, Observable<any[]>>();

  constructor(http: HttpClient) {
    super(http, 'offer-trips');
  }

  peekCachedUserTrips(id: number | null): any[] | null {
    if (id == null || Number.isNaN(Number(id))) return null;
    return this.userTripsCache.get(Number(id)) ?? null;
  }

  peekCachedUserTripsAll(id: number | null): any[] | null {
    if (id == null || Number.isNaN(Number(id))) return null;
    return this.userTripsAllCache.get(Number(id)) ?? null;
  }

  clearUserTripsCache(id?: number | null) {
    if (id == null || Number.isNaN(Number(id))) {
      this.userTripsCache.clear();
      this.userTripsAllCache.clear();
      this.userTripsInFlight.clear();
      this.userTripsAllInFlight.clear();
      return;
    }
    const key = Number(id);
    this.userTripsCache.delete(key);
    this.userTripsAllCache.delete(key);
    this.userTripsInFlight.delete(key);
    this.userTripsAllInFlight.delete(key);
  }

  private cacheUserTripsRequest(
    id: number | null,
    cache: Map<number, any[]>,
    inFlight: Map<number, Observable<any[]>>,
    url: string,
    fallbackMessage: string,
  ): Observable<any[]> {
    const key = Number(id);
    const pending = inFlight.get(key);
    if (pending) return pending;

    const request$ = this.http
      .get<any[]>(url, {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      })
      .pipe(
        tap((trips) => cache.set(key, trips || [])),
        catchError((err: HttpErrorResponse) =>
          throwError(() => new Error(err.error?.message || fallbackMessage)),
        ),
        finalize(() => {
          if (inFlight.get(key) === request$) inFlight.delete(key);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    inFlight.set(key, request$);
    return request$;
  }
  getTripById(id: string, userId?: number | null) {
    let url = `${this.actionUrl()}/trips/${id}`;
    if (userId) {
      url += `?user_id=${userId}`;
    }

    return this.http.get<any>(url, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }

  /** ADMIN LIST: GET /api/offer-trips/all */
  getAllAdmin(params?: {
    search?: string;
    driver?: number | string;
    car?: number | string;
    from?: string;
    to?: string;
    per_page?: number | string;
    page?: number | string;
    with_trashed?: boolean | number | string;
  }): Observable<any> {
    let p = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') p = p.set(k, String(v));
      });
    }
    return this.http
      .get<any>(`${this.apiServerUrl}/admin/offered-trips`, {
        params: p,
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /** RESTORE: PATCH /api/offer-trips/{id}/restore */
  restoreTrip(id: number): Observable<any> {
    return this.http
      .patch(`${this.apiServerUrl}/offer-trips/${id}/restore`, {})
      .pipe(
        tap(() => this.clearUserTripsCache()),
        catchError(this.handleError.bind(this)),
      );
  }

  addOfferedTrip(data: any): Observable<AddOfferedTripResponse> {
    return this.http
      .post<AddOfferedTripResponse>(
        this.actionUrl() + '/add-offered-trip',
        data,
      )
      .pipe(
        tap(() => this.clearUserTripsCache()),
        catchError((err: HttpErrorResponse) => {
          // 👉 KEEP the original HTTP error so frontend can read err.status = 409
          return throwError(() => err);
        }),
      );
  }

  searchRides(data: any): Observable<any[]> {
    return this.http
      .post<any[]>(this.actionUrl() + '/search-rides', data)
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to search rides'),
          ),
        ),
      );
  }

  getLatestTrips(limit = 15): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.actionUrl()}/latest?limit=${limit}`, {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      })
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to get latest trips'),
          ),
        ),
      );
  }

  /** show by id (keep if used elsewhere) */
  getUserTrips(id: number | null): Observable<any[]> {
    return this.cacheUserTripsRequest(
      id,
      this.userTripsCache,
      this.userTripsInFlight,
      `${this.actionUrl()}/users/${id}`,
      'Failed to get user trips',
    );
  }

  /** current user's trips */
  getUserTripsAll(userId?: number | null): Observable<any[]> {
    return this.cacheUserTripsRequest(
      userId ?? null,
      this.userTripsAllCache,
      this.userTripsAllInFlight,
      `${this.actionUrl()}/user-all-trips/${userId}`,
      'Failed to get user trips',
    );
  }

  updateTrip(id: number, tripData: any): Observable<any> {
    return this.http
      .put(`${this.actionUrl()}/update/${id}`, tripData)
      .pipe(
        tap(() => this.clearUserTripsCache()),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to update trip'),
          ),
        ),
      );
  }

  /** maps to backend DELETE /offer-trips/{id} */
  deleteTrip(id: number): Observable<any> {
    return this.http
      .delete(`${this.actionUrl()}/${id}`)
      .pipe(
        tap(() => this.clearUserTripsCache()),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to delete trip'),
          ),
        ),
      );
  }
}

/* ============================================================
 * Reserved Trips
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class ReservedTripService extends AbstractRestService<ReservedTrip> {
  private reservedTripsCache = new Map<number, any[]>();
  private reservedTripsInFlight = new Map<number, Observable<any[]>>();

  constructor(http: HttpClient) {
    super(http, 'reserve-trips');
  }

  peekCachedUserReserveTrips(userId: number | null): any[] | null {
    if (userId == null || Number.isNaN(Number(userId))) return null;
    return this.reservedTripsCache.get(Number(userId)) ?? null;
  }

  clearReservedTripsCache(userId?: number | null) {
    if (userId == null || Number.isNaN(Number(userId))) {
      this.reservedTripsCache.clear();
      this.reservedTripsInFlight.clear();
      return;
    }
    const key = Number(userId);
    this.reservedTripsCache.delete(key);
    this.reservedTripsInFlight.delete(key);
  }

  getResTripById(id: string, userId?: number | null) {
    let url = `${this.actionUrl()}/Restrips/${id}`;
    if (userId) {
      url += `?user_id=${userId}`;
    }
    return this.http.get<any>(url, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }
  // Update reservation status
  updateStatus(
    reservationId: number,
    status: 'accepted' | 'pending' | 'rejected',
    linkedReservationId?: number | null,
  ): Observable<any> {
    const payload: {
      status: 'accepted' | 'pending' | 'rejected';
      linked_reservation_id?: number;
    } = {
      status,
    };

    if (linkedReservationId) {
      payload.linked_reservation_id = linkedReservationId;
    }

    return this.http.post(`${this.actionUrl()}/${reservationId}/status`, payload).pipe(
      tap(() => this.clearReservedTripsCache()),
    );
  }

  reserveTrip(data: any): Observable<any> {
    return this.http
      .post(`${this.actionUrl()}/`, data)
      .pipe(
        tap(() => this.clearReservedTripsCache()),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to reserve trip'),
          ),
        ),
      );
  }

  reserveTrips(data: any[]): Observable<any> {
    return this.http
      .post(`${this.actionUrl()}/batch`, { reservations: data })
      .pipe(
        tap(() => this.clearReservedTripsCache()),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.message || 'Failed to reserve trips'),
          ),
        ),
      );
  }

  getUserReserveTrips(userId: number | null): Observable<any[]> {
    const key = Number(userId);
    const pending = this.reservedTripsInFlight.get(key);
    if (pending) return pending;

    const request$ = this.http
      .get<any[]>(`${this.actionUrl()}/all-reserved/${userId}`, {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
      })
      .pipe(
        tap((trips) => this.reservedTripsCache.set(key, trips || [])),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () =>
              new Error(
                err.error?.message || 'Failed to get user reserved trips',
              ),
          ),
        ),
        finalize(() => {
          if (this.reservedTripsInFlight.get(key) === request$) {
            this.reservedTripsInFlight.delete(key);
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.reservedTripsInFlight.set(key, request$);
    return request$;
  }

  deleteRserveTrip(id: number): Observable<any> {
    // keep name for compatibility
    return this.http
      .delete(`${this.actionUrl()}/${id}`, {})
      .pipe(
        tap(() => this.clearReservedTripsCache()),
        catchError((err: HttpErrorResponse) =>
          throwError(
            () =>
              new Error(err.error?.message || 'Failed to delete reserve trip'),
          ),
        ),
      );
  }

  sendReservationEmail(userId: number, message: string): Observable<any> {
    const body = { user_id: userId, message };
    return this.http.post(`${this.actionUrl()}/send-reservation-email`, body);
  }
}

export interface DeviceToken {
  user_id: number;
  device_token: string;
  platform: string;
}

export interface NotificationData {
  id: number;
  user_id: number;
  title: string;
  body: string;
  data?: any;
  read?: boolean; // For clicked notifications
  seen?: boolean; // For unclickable notifications
  created_at: string;
  updated_at: string;
}
@Injectable({ providedIn: 'root' })
export class PushTokenService extends AbstractRestService<DeviceToken> {
  constructor(http: HttpClient) {
    super(http, 'device-tokens'); // endpoint
  }

  // SAVE / CREATE token
  saveToken(tokenData: DeviceToken): Observable<DeviceToken> {
    return this.http
      .post<DeviceToken>(this.actionUrl(), tokenData)
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () =>
              new Error(err.error?.message || 'Failed to save device token'),
          ),
        ),
      );
  }

  /** Fetch only unread notifications */
  getUnreadNotifications(): Observable<NotificationData[]> {
    return this.http.get<NotificationData[]>(`${this.actionUrl()}/unread`);
  }

  /** Mark all notifications as read */
  markAllRead(): Observable<any> {
    return this.http.post(`${this.actionUrl()}/mark-all-read`, {});
  }
  deleteToken(payload: { user_id: number | null; device_token: string }) {
    return this.http.post(`${this.actionUrl()}/delete-token`, payload);
  }

  markNotificationRead(
    notification: number | { id?: number; notification_id?: string },
  ) {
    const payload =
      typeof notification === 'number' ? { id: notification } : notification;
    return this.http.post(`${this.actionUrl()}/mark-read`, payload);
  }
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService extends AbstractRestService<UserSubscription> {
  constructor(http: HttpClient) {
    super(http, 'trip-subscriptions'); // your backend endpoint
  }

  // Subscribe to a route explicitly with HttpClient
  subscribeToRoute(data: any): Observable<any> {
    // use any instead of UserSubscription
    const payload = { ...data }; // plain object, no class casting
    return this.http.post<any>(this.actionUrl(), payload).pipe(
      catchError((error) => {
        console.error('HTTP error in subscribeToRoute', error);
        return throwError(() => error); // <-- propagate error to caller
      }),
    );
  }

  // Unsubscribe
  unsubscribeRoute(id: number): Observable<any> {
    return this.http
      .delete(`${this.actionUrl()}/${id}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  getUserSubscriptions(userId?: string) {
    let params = {};
    if (userId) {
      params = { userID: userId };
    }

    return this.http.get(`${this.actionUrl()}/user`, { params: params }).pipe(
      map((res: any) => res),
      catchError((err: HttpErrorResponse) =>
        throwError(
          () => new Error(err.error?.message || 'Failed to get subscriptions'),
        ),
      ),
    );
  }

  updateReservationStatus(
    reservationId: number,
    status: 'accepted' | 'declined',
  ): Observable<any> {
    const payload = { reservation_id: reservationId, status };

    return this.http
      .post<any>(`${this.actionUrl()}/update-reservation-status`, payload)
      .pipe(
        catchError((error) => {
          console.error('HTTP error in updateReservationStatus', error);
          return throwError(() => error);
        }),
      );
  }
}

/* ============================================================
 * Trip Reviews
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class TripReviewService extends AbstractRestService<TripReview> {
  constructor(http: HttpClient) {
    super(http, 'trip-reviews');
  }

  // Get reviews by current user
  getUserReviews(): Observable<TripReview[]> {
    return this.http
      .get<TripReview[]>(`${this.actionUrl()}/user`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Get reviews for a specific driver
  getDriverReviews(driverId: number): Observable<any> {
    return this.http
      .get<any>(`${this.actionUrl()}/driver/${driverId}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Get review for a specific trip
  getTripReview(reservedTripId: number): Observable<TripReview> {
    return this.http
      .get<TripReview>(`${this.actionUrl()}/trip/${reservedTripId}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Submit a new review
  submitReview(review: Partial<TripReview>): Observable<any> {
    return this.http
      .post<any>(this.actionUrl(), review)
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Update a review
  updateReview(id: number, review: Partial<TripReview>): Observable<any> {
    return this.http
      .put<any>(`${this.actionUrl()}/${id}`, review)
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Delete a review
  deleteReview(id: number): Observable<any> {
    return this.http
      .delete(`${this.actionUrl()}/${id}`)
      .pipe(catchError(this.handleError.bind(this)));
  }
}

/* ============================================================
 * Favorite Drivers
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class FavoriteDriverService extends AbstractRestService<FavoriteDriver> {
  constructor(http: HttpClient) {
    super(http, 'favorite-drivers');
  }

  // Get all favorite drivers
  getFavorites(): Observable<any[]> {
    return this.http
      .get<any[]>(this.actionUrl())
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Add a driver to favorites
  addFavorite(driverId: number): Observable<any> {
    return this.http
      .post<any>(this.actionUrl(), { driver_id: driverId })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Remove a driver from favorites
  removeFavorite(driverId: number): Observable<any> {
    return this.http
      .delete(`${this.actionUrl()}/${driverId}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  // Check if a driver is favorited
  isFavorite(driverId: number): Observable<{ is_favorite: boolean }> {
    return this.http
      .get<{
        is_favorite: boolean;
      }>(`${this.actionUrl()}/check/${driverId}`)
      .pipe(catchError(this.handleError.bind(this)));
  }
}
